<template>
  <!-- 复用配置 -->
  <template v-if="doReuseConfig">
    <span
      class="text-sm cursor-pointer p-2"
      @click="handleReuseConfig"
    >{{ $t('common.reuse_config') }}</span>
    <template v-if="hasNextAction('reuseConfig')">
      <div class="w-px h-4 bg-fill-7" />
    </template>
  </template>

  <!-- 收藏 -->
  <template v-if="doFavorite">
    <el-tooltip
      :content="isBookmarked ? $t('common.unfavorite') : $t('common.favorites')"
      placement="top"
    >
      <button
        class="p-2 outline-none"
        @click="handleFavorite"
      >
        <Icon
          v-if="!isBookmarked"
          icon="sky:ic-star"
          class="w-4 h-4"
        />
        <Icon
          v-else
          icon="sky:ic-star-fill"
          class="w-4 h-4 text-yellow-6"
        />
      </button>
    </el-tooltip>
    <template v-if="hasNextAction('favorite')">
      <div class="w-px h-4 bg-fill-7" />
    </template>
  </template>

  <!-- 审核 -->
  <template v-if="doAudit">
    <el-tooltip
      :content="$t('common.select_action')"
      placement="top"
    >
      <button
        class="p-2 outline-none"
        :class="{ 'cursor-not-allowed text-text-4': auditStatus === AUDIT_STATUS.PASSED || auditStatus === AUDIT_STATUS.AUDITING }"
        :disabled="auditStatus === AUDIT_STATUS.PASSED || auditStatus === AUDIT_STATUS.AUDITING"
        @click="handleAudit"
      >
        <Icon
          icon="sky:ic-arraignment"
          class="w-4 h-4"
        />
      </button>
    </el-tooltip>
    <template v-if="hasNextAction('audit')">
      <div class="w-px h-4 bg-fill-7" />
    </template>
  </template>

  <!-- 选取 -->
  <template v-if="doSelect">
    <el-tooltip
      :content="$t('common.select')"
      placement="top"
    >
      <button
        class="p-1 outline-none"
        @click="handleSelect"
      >
        <Icon
          :icon="selected === 1 ? 'sky:ic-check-circle-fill' : 'sky:ic-radio'"
          class="size-4 flex-shrink-0"
          :class="selected === 1 ? 'text-brand-6' : 'text-text-2'"
        />
      </button>
    </el-tooltip>
    <template v-if="hasNextAction('select')">
      <div class="w-px h-4 bg-fill-7" />
    </template>
  </template>
  <!-- 下载 -->
  <template v-if="doDownload">
    <el-tooltip
      :content="$t('common.download')"
      placement="top"
    >
      <button
        class="p-2 outline-none"
        @click="handleDownload"
      >
        <Icon
          v-if="!loading"
          icon="sky:ic-down-tray"
          class="w-4 h-4"
        />
        <Icon
          v-else
          icon="sky:ic-loading"
          class="w-4 h-4 animate-spin"
        />
      </button>
    </el-tooltip>
    <template v-if="hasNextAction('download')">
      <div class="w-px h-4 bg-fill-7" />
    </template>
  </template>

  <!-- 裁剪 -->
  <template v-if="doClipper">
    <el-tooltip
      :content="$t('common.crop')"
      placement="top"
    >
      <button
        class="p-2 outline-none"
        @click="handleClipper"
      >
        <Icon
          icon="sky:ic-clipper"
          class="w-4 h-4"
        />
      </button>
    </el-tooltip>
    <template v-if="hasNextAction('clipper')">
      <div class="w-px h-4 bg-fill-7" />
    </template>
  </template>
  <!-- 更多 -->
  <!-- <template v-if="doHd || doRepainting">
    <el-dropdown>
      <button
        class="p-2 outline-none"
      >
        <Icon
          icon="sky:ic-ellipsis-horizontal"
          class="w-4 h-4"
        />
      </button>

      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            v-if="doHd"
            data-value="hd"
            @click="handleHdClick"
          >
            <Icon
              icon="sky:ic-hd"
              class="w-4 h-4"
            />
            <span class="ml-2">{{ $t('common.upscale') }}</span>
          </el-dropdown-item>
          <el-dropdown-item
            v-if="doRepainting"
            data-value="repainting"
            @click="handleRepaintingClick"
          >
            <Icon
              icon="sky:ic-repaint-fill"
              class="w-4 h-4"
            />
            <span class="ml-2">{{ $t('common.inpainting') }}</span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </template> -->
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePermissionStore } from '@/stores/permission-store'
import { AUDIT_STATUS } from '@/api/type'
import { Icon } from '@iconify/vue'

const { t } = useI18n()
const permissionStore = usePermissionStore()

type Props = {
  bookmark?: 0 | 1 | boolean
  selected?: 0 | 1
  loading?: boolean
  auditStatus?: AUDIT_STATUS
  doReuseConfig?: () => void
  doFavorite?: () => void
  doAudit?: () => void
  doDownload?: () => void
  doHd?: () => void
  doRepainting?: () => void
  doClipper?: () => void
  doSelect?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  bookmark: false,
  loading: false,
  selected: 0,
})

const actionKeys = ['reuseConfig', 'favorite', 'audit', 'select', 'download', 'clipper', 'hd', 'repainting', 'select'] as const

const hasNextAction = (currentAction: (typeof actionKeys)[number]) => {
  const currentIndex = actionKeys.indexOf(currentAction)
  if (currentIndex === -1 || currentIndex === actionKeys.length - 1) {
    return false
  }
  const callbackMap = {
    reuseConfig: props.doReuseConfig,
    favorite: props.doFavorite,
    audit: props.doAudit,
    download: props.doDownload,
    clipper: props.doClipper,
    hd: props.doHd,
    repainting: props.doRepainting,
    select: props.doSelect,
  }
  for (let i = currentIndex + 1; i < actionKeys.length; i++) {
    if (callbackMap[actionKeys[i]]) {
      return true
    }
  }
  return false
}

const isBookmarked = computed(() => {
  if (typeof props.bookmark === 'number') {
    return props.bookmark === 1
  }
  return Boolean(props.bookmark)
})

const handleReuseConfig = () => {
  props.doReuseConfig?.()
}

const handleFavorite = () => {
  props.doFavorite?.()
}

const handleAudit = () => {
  props.doAudit?.()
}

const handleDownload = () => {
  props.doDownload?.()
}

const handleHdClick = () => {
  props.doHd?.()
}

const handleRepaintingClick = () => {
  props.doRepainting?.()
}

const handleClipper = () => {
  props.doClipper?.()
}

const handleSelect = () => {
  props.doSelect?.()
}
</script>
